import React from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { ibeAccountManagerRead } from 'actions/salon/beAccountManager.action';
import NO_IMAGE from 'assets/images/online-booking/no-image-available.png';
import IMG_BUTTON from 'assets/images/support/button-arrow.png';
import IMG_ENVELOPE from 'assets/images/support/envelope-solid.svg';
import IMG_HELP_CENTER from 'assets/images/support/help-center.webp';
import IMG_CUSTOMER from 'assets/images/support/ico-customer.png';
import IMG_MANAGEMENT from 'assets/images/support/ico-management.png';
import IMG_TIPS from 'assets/images/support/ico-tips.png';
import IMG_PHONE from 'assets/images/support/phone-solid.svg';
import IMG_PLAN from 'assets/images/support/plan-illustration.png';
import IMG_QR_CODE from 'assets/images/support/qr-code.png';
import IMG_TEAM from 'assets/images/support/team.jpg';
import Showcase from 'components/account/component/Showcase.component';
import Title from 'components/account/component/Title.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Space from 'components/generic/Space.component';
import Video from 'components/generic/Video.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import { COUNTRY_CODES } from 'constants/address/counties.const';
import S from 'constants/string.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useWindowScroll from 'hooks/useWindowScroll.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonCountryCode } from 'selectors/currentSalon.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';

import TopNav from 'common/TopNav.component';

import CN from './SupportPage.module.scss';

const LINK_MAIL = 'mailto:support@getslick.com';
const SUPPORT_EMAIL = 'support@getslick.com';
const SUPPORT_PHONE = '01202 286 005';
const SUPPORT_PHONE_TIME = '9am - 5pm';
const LINK_PHONE = 'tel:01202286005';
const LINK_ROADMAP = 'https://slick.sleekplan.app/roadmap';
const LINK_PLAN = 'https://slick.sleekplan.app/';
const LINK_WHATSAPP = 'https://api.whatsapp.com/message/4S4PTWAYL7IEB1?autoload=1&app_absent=0';
const LINK_VIDEO = 'https://www.youtube.com/embed/lgavPTDV6b0?si=eWIcgc4-NYmtVfkM';

const RENDER_VIDEO = false;

const SupportPage = () => {
  useWindowScroll(0, 0);

  const { t } = useTranslation();
  const { qj } = usePrepareReadOnly(ibeAccountManagerRead);

  const countryCode = useSelector(selectCurrentSalonCountryCode);
  const isGBSalon = countryCode === COUNTRY_CODES.GB;

  const name = fromAnyToString(qj?.name) || t('accountManagement.noAssigned');
  const image = qj?.profileImageUrl || NO_IMAGE;
  const link = fromAnyToString(qj?.link);

  const renderManagerSection = !!(name && link);

  return (
    <div data-bem="SupportPage" className={CN.component}>
      <TopNav title={t('accountManagement.pageTitle')}>
        <SidebarMenu />
      </TopNav>

      <div className={CN.wrap}>
        <div data-bem="SupportPage__hero-banner" className={CN.heroBanner}>
          <div className={CN.bannerHeaderContainer}>
            <div className={CN.topBannerTitle}>
              {t('accountManagement.pageTitle')} {S.womanTipping}
            </div>
            {t('accountManagement.subtitle')}
          </div>
          <div className={CN.heroImgContainer}>
            <div className={CN.bannerGradient} />
          </div>
        </div>

        <Title tag="SupportPage" img={IMG_CUSTOMER} text={t('accountManagement.customerSupport')} />

        <div
          data-bem="SupportPage__support-card"
          className={cn({
            [CN.bannerCard]: true,
            [CN.withGradient]: true,
          })}
        >
          <div
            className={cn({
              [CN.contentBox]: true,
              [CN.inverted]: true,
            })}
          >
            {t('accountManagement.description1')}
          </div>
          <h2 className={CN.subTitle}>
            {S.raisedHands} {t('accountManagement.quickestWayToContact')}
          </h2>
          <div
            className={cn({
              [CN.contactMethod]: true,
              [CN.byChat]: true,
            })}
          >
            <div className={CN.iconHelp}>
              <img src={IMG_HELP_CENTER} alt="Help center" className={CN.helpImg} />
              <h3>
                {t('accountManagement.liveChat')}
                <span className={CN.subInfo}>{t('accountManagement.responseTime')}</span>
              </h3>
            </div>
            <div className={CN.qrCodeInfo}>
              <ResponsiveBox.Desktop>
                <div className={CN.qrCodeText}>
                  <div className={CN.smallText}>{t('accountManagement.useChatBotQrDesktop')}</div>
                  <img src={IMG_QR_CODE} alt="QR code" className={CN.qrCode} />
                </div>
              </ResponsiveBox.Desktop>
              <ResponsiveBox.Mobile>
                <div className={CN.qrCodeText}>
                  <div className={CN.smallText}>
                    <Space>{t('accountManagement.useChatBotQrMobile')}</Space>
                    <ExternalLink link={LINK_WHATSAPP} variant="inline">
                      <div className={CN.link}> {t('accountManagement.whatsappService')}</div>
                    </ExternalLink>
                  </div>
                </div>
              </ResponsiveBox.Mobile>
            </div>
          </div>
          <h2 className={CN.subTitle}>
            {S.megaphone} {t('accountManagement.alternativeWayToContact')}
          </h2>
          <div className={CN.contactOptions}>
            <div className={CN.contactMethod}>
              <div className={CN.iconsContainer}>
                <img src={IMG_ENVELOPE} alt="Envelop" className={CN.icon} />
              </div>
              <h3>
                {t('accountManagement.emailSupport')}
                <span className={CN.subInfo}>{t('accountManagement.emailResponseTime')}</span>
                <ExternalLink link={LINK_MAIL} variant="inline">
                  <span className={CN.mail}>{SUPPORT_EMAIL}</span>
                </ExternalLink>
              </h3>
            </div>
            {/* TODO: Change when there is more info about US phone support */}
            {isGBSalon && (
              <div className={CN.contactMethod}>
                <div className={CN.iconsContainer}>
                  <img src={IMG_PHONE} alt="Phone" className={CN.icon} />
                </div>
                <h3>
                  {t('accountManagement.phoneSupport')}
                  <span className={CN.subInfo}>{t('accountManagement.phoneResponseTime')}</span>
                  <ExternalLink link={LINK_PHONE} variant="inline">
                    <span className={CN.pmTime}>
                      {SUPPORT_PHONE_TIME} {S.dot}
                    </span>
                    {S.space}
                    <span className={CN.mail}> {SUPPORT_PHONE}</span>
                  </ExternalLink>
                </h3>
              </div>
            )}
          </div>
        </div>

        <div data-bem="SupportPage__panel-product-features" className={CN.panelProductFeatures}>
          <div className={CN.imgPanelContainer}>
            <img src={IMG_PLAN} alt="Phone" className={CN.imgPanel} />
          </div>
          <div className={CN.textContainer}>
            <div className={CN.textBolder}>
              <ExternalLink link={LINK_ROADMAP} variant="inline">
                <span className={CN.linkBold}>{t('accountManagement.viewProduct')}</span>
              </ExternalLink>
              <Space>{t('accountManagement.comingSoon')}</Space>
            </div>

            <ResponsiveBox.Desktop>
              <div>
                <Space>{t('accountManagement.description2.part1')}</Space>
                <ExternalLink link={LINK_PLAN} variant="inline">
                  <span className={CN.link}>
                    <Space>{t('accountManagement.description2.part2')} </Space>
                  </span>
                </ExternalLink>
                {t('accountManagement.description2.part3')}
              </div>
            </ResponsiveBox.Desktop>
          </div>
          <div className={CN.panelButtonContainer}>
            <ExternalLink link={LINK_PLAN} variant="inline">
              <img src={IMG_BUTTON} alt="Phone" className={CN.imgButton} />
            </ExternalLink>
          </div>
        </div>

        <Title tag="SupportPage" img={IMG_MANAGEMENT} text={t('accountManagement.accountManagementTitle')} />

        <div data-bem="SupportPage__account-manager" className={CN.bannerCard}>
          <div className={CN.topSection}>
            <div className={CN.leftBox}>
              <h2 className={CN.subTitle}>{t('accountManagement.box1Title')}</h2>
              <div className={CN.contentBox}>
                <h3 className={CN.textTitle}>{t('accountManagement.productTrainingTitle')}</h3>
                {t('accountManagement.box1Description1')}
                <h3 className={CN.textTitle}>{t('accountManagement.businessCoachingTitle')}</h3>
                {t('accountManagement.box1Description2')}
              </div>
            </div>

            <div className={CN.rightBox}>
              <h2 className={CN.subTitle}>{t('accountManagement.box2Title')}</h2>

              <div className={CN.videoThumbnail}>
                {RENDER_VIDEO ? <Video src={LINK_VIDEO} title={t('accountManagement.videoTitle')} /> : <img src={IMG_TEAM} alt="Team" className={CN.videoImage} />}
              </div>
            </div>
          </div>

          <div className={CN.bottomSection}>
            <h2 className={CN.subTitle}>{t('accountManagement.box3Title')}</h2>
            <div className={CN.contentBox}>
              <h3 className={CN.textTitle}>{t('accountManagement.box3Subtitle')}</h3>
              {t('accountManagement.box3Description')}
            </div>
          </div>
        </div>

        {renderManagerSection && (
          <div data-bem="SupportPage__book-call" className={CN.bannerBook}>
            <div className={CN.avatarContainer}>
              <div className={CN.profileImgContainer}>
                <img src={image} alt="hone" className={CN.avatar} />
              </div>
              <div className={CN.userInfo}>
                <div>
                  <span className={CN.userRole}>{t('accountManagement.yourAccountManager')}</span>
                </div>
                <span className={CN.userName}>{name}</span>
              </div>
            </div>

            <ExternalLink link={link} variant="inline">
              <div className={CN.bookButtonContainer}>
                <Icon variant="phoneOutline" width="lg" />
                <span className={CN.bookButtonText}>{t('accountManagement.bookCall')}</span>
              </div>
            </ExternalLink>
          </div>
        )}

        <Title tag="SupportPage" img={IMG_TIPS} text={t('accountManagement.supportTitle')} />

        <div data-bem="SupportPage__showcase" className={CN.bannerCard}>
          <h2 className={CN.subTitle}>{t('accountManagement.supportSubtitle')}</h2>
          <Showcase tag="SupportPage" />
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
